import React, { useCallback, useEffect, useRef, useState } from 'react';
import PanResponder from 'react-panresponder-web';
import Animated from 'animated/lib/targets/react-dom';
import SSideMenu from 'shared/components/SideMenu';
import Screens from 'shared/screens';
import { getText, TEXT_KEY } from 'shared/utils/localization';
import { FAVOURITE_ONBOARDING_STEPS } from '../OnboardingLayer/onboardingSteps';
import { BlackOverlay, Container, MenuContainer, Toggler } from './styledComponents';
import useCheckSidebarZindex from './hooks/useCheckSidebarZindex';
import MenuItem from './components/MenuItem';

const SideMenu = ({ onboardingStep, setOnboardingHighlightedPosition, navigation }) => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const zIndex = useCheckSidebarZindex({ isMenuVisible });
  const [menuWidth, setMenuWidth] = useState(0);
  const menuRef = useRef();
  const favouritesOption = useRef(null);
  const prevOnboardingStepRef = useRef();

  useEffect(() => {
    if (
      onboardingStep !== prevOnboardingStepRef.current &&
      onboardingStep === FAVOURITE_ONBOARDING_STEPS.GO_TO_FAVOURITES
    ) {
      const { x, y, width, height } = favouritesOption.current.getBoundingClientRect();
      setOnboardingHighlightedPosition({ x, y, width, height }, { message: getText(TEXT_KEY.GAME_ADDED_TO_FAVS) });
    }

    prevOnboardingStepRef.current = onboardingStep;
  }, [onboardingStep, setOnboardingHighlightedPosition]);

  useEffect(() => {
    if (menuRef.current) setMenuWidth(menuRef.current?.offsetWidth);
  }, [menuRef.current?.offsetWidth, isMenuVisible]);

  useEffect(() => {
    const handleResize = () => {
      if (menuRef.current) setMenuWidth(menuRef.current?.offsetWidth);
    };

    window.addEventListener('resize', handleResize);
    window.matchMedia('(orientation: landscape)').addEventListener('change', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.matchMedia('(orientation: landscape)').removeEventListener('change', handleResize);
    };
  }, []);

  const isRouteActive = useCallback(route => {
    return document.location.href.indexOf(route) !== -1;
  }, []);

  const createMenuItemsConfig = useCallback(
    args => {
      const baseItems = [
        {
          screen: Screens.Favourites,
          roomName: getText(TEXT_KEY.SIDE_BAR_FAVORITE),
          active: isRouteActive('favourites'),
          disabled: !args.userCanFavourite,
          ref: favouritesOption,
        },
        {
          screen: Screens.Home,
          roomName: getText(TEXT_KEY.SIDE_BAR_SLOT),
          active: isRouteActive('slots'),
        },
        {
          screen: Screens.VIP,
          roomName: getText(TEXT_KEY.SIDE_BAR_VIP_ROOM),
          active: isRouteActive('vip'),
        },
        {
          screen: Screens.Tables,
          roomName: getText(TEXT_KEY.SIDE_BAR_TABLE),
          active: isRouteActive('table'),
        },
      ];

      return baseItems;
    },
    [isRouteActive]
  );

  return (
    <SSideMenu
      navigation={navigation}
      animated={Animated}
      panResponder={PanResponder}
      render={args => {
        const { expanded, handleTouchedHandler, hasSportsRoom, dynamicGameRoomsEnabled, gameRooms } = args;

        const menuItems = dynamicGameRoomsEnabled ? gameRooms : createMenuItemsConfig(args);
        setIsMenuVisible(expanded);

        return (
          <>
            <BlackOverlay onClick={expanded ? handleTouchedHandler : undefined} isVisible={expanded} />
            <Container $zIndex={zIndex} $isMenuVisible={expanded} menuWidth={menuWidth}>
              <MenuContainer ref={menuRef} hasSportsRoom={hasSportsRoom}>
                {menuItems.map((item, index) => (
                  <MenuItem key={index} args={args} item={item} itemsLength={menuItems.length} />
                ))}
              </MenuContainer>
              <Toggler isMenuVisible={expanded} alt="" onClick={handleTouchedHandler} />
            </Container>
          </>
        );
      }}
    />
  );
};

export default SideMenu;

